import { MODULES_BY_KEY, type ModuleKey } from '@ruby/shared';
import { useNavigate } from '@tanstack/react-router';
import { CornerDownLeft, Search } from 'lucide-react';
import { Dialog as D } from 'radix-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MODULE_UI } from '@/app/modules';
import { visibleMenus } from '@/lib/permissions';
import { useSession } from '@/lib/session';
import { cn } from '@/lib/utils';

export function useCommandPalette() {
  const [isOpen, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return { isOpen, setOpen, open: useCallback(() => setOpen(true), []) };
}

interface Entry {
  module: ModuleKey;
  screen?: string;
  label: string;
  context: string;
}

/** Ctrl+K: jump to any screen the user can open, across modules. */
export function CommandPalette({ isOpen, setOpen }: { isOpen: boolean; setOpen(v: boolean): void }) {
  const boot = useSession((s) => s.boot);
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);

  const entries = useMemo<Entry[]>(() => {
    if (!boot) return [];
    return boot.modules.flatMap((m) => {
      const def = MODULES_BY_KEY[m];
      return [
        { module: m, label: `${def.name} dashboard`, context: def.name },
        ...visibleMenus(boot.permissions, m).flatMap((menu) =>
          menu.screens.filter((s) => !s.hidden || s.hub).map((s) => ({ module: m, screen: s.key, label: s.label, context: `${def.name} › ${menu.label}` })),
        ),
      ];
    });
  }, [boot]);

  const results = useMemo(() => {
    const terms = q.toLowerCase().split(/\s+/).filter(Boolean);
    return entries.filter((e) => terms.every((t) => `${e.label} ${e.context}`.toLowerCase().includes(t))).slice(0, 12);
  }, [entries, q]);

  useEffect(() => setActive(0), [q]);
  useEffect(() => {
    if (!isOpen) setQ('');
  }, [isOpen]);

  const go = (e: Entry) => {
    setOpen(false);
    if (e.screen) void navigate({ to: '/m/$module/$screen', params: { module: e.module, screen: e.screen } });
    else void navigate({ to: '/m/$module', params: { module: e.module } });
  };

  return (
    <D.Root open={isOpen} onOpenChange={setOpen}>
      <D.Portal>
        <D.Overlay className="fx-overlay fixed inset-0 z-50 bg-slate-950/40 backdrop-blur-[3px]" />
        <D.Content className="fx-dialog fixed top-[15vh] left-1/2 z-50 w-[calc(100vw-2rem)] max-w-xl -translate-x-1/2 overflow-hidden rounded-xl border border-border bg-surface shadow-(--shadow-pop)">
          <D.Title className="sr-only">Find a screen</D.Title>
          <D.Description className="sr-only">Type to search screens across your modules</D.Description>
          <div className="flex items-center gap-2 border-b border-border px-4">
            <Search className="size-4 text-fg-subtle" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search screens, e.g. “sales orders”, “roles”…"
              className="h-12 w-full bg-transparent text-sm outline-none"
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setActive((a) => Math.min(a + 1, results.length - 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setActive((a) => Math.max(a - 1, 0));
                } else if (e.key === 'Enter' && results[active]) go(results[active]);
              }}
            />
          </div>
          <ul className="max-h-80 overflow-y-auto p-1.5">
            {results.length === 0 && <li className="px-3 py-6 text-center text-sm text-fg-muted">No matching screens</li>}
            {results.map((e, i) => {
              const Icon = MODULE_UI[e.module].icon;
              return (
                <li key={`${e.module}.${e.screen ?? ''}`}>
                  <button
                    onMouseEnter={() => setActive(i)}
                    onClick={() => go(e)}
                    className={cn('flex w-full items-center gap-3 rounded-md px-3 py-2 text-left', i === active && 'bg-primary-soft')}
                  >
                    <Icon className="size-4 text-fg-subtle" />
                    <span className="flex-1">
                      <span className="block text-sm font-medium">{e.label}</span>
                      <span className="block text-xs text-fg-subtle">{e.context}</span>
                    </span>
                    {i === active && <CornerDownLeft className="size-4 text-fg-subtle" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}
